import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Flame, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiErrorDetail } from "@/context/AuthContext";

const EASE = [0.16, 1, 0.3, 1];

const encouragement = (pct, streak) => {
  if (pct >= 100) return "MashaAllah, guide terminé ! Qu'Allah ancre ta mémorisation.";
  if (streak >= 30) return "SubhanAllah, un mois de régularité — tu es inarrêtable.";
  if (streak >= 7) return `${streak} jours d'affilée — la constance paie, continue !`;
  if (streak >= 3) return `${streak} jours de suite, belle lancée !`;
  if (pct >= 50) return "Plus de la moitié du chemin — courage !";
  if (pct > 0) return "Chaque jour compte — un verset après l'autre.";
  return "Coche ton premier jour pour lancer ta série.";
};

const milestoneToast = (pct) => {
  if (pct >= 100) return "Guide terminé — MashaAllah !";
  if (pct >= 75) return "75 % — plus qu'un quart d'effort !";
  if (pct >= 50) return "50 % — tu es à mi-chemin !";
  if (pct >= 25) return "25 % — belle progression !";
  return null;
};

export const ProgressTracker = ({ data }) => {
  const [done, setDone] = useState(() => new Set(data.done));
  const [streak, setStreak] = useState(data.streak);
  const [upto, setUpto] = useState("");
  const pct = Math.round((done.size / data.total) * 100);

  const toggle = async (day) => {
    const prev = done;
    const next = new Set(done);
    const wasChecked = next.has(day);
    if (wasChecked) next.delete(day);
    else next.add(day);
    setDone(next);
    try {
      const r = await axios.post(`${API}/progress/toggle`, { product: data.product, day }, { withCredentials: true });
      setStreak(r.data.streak);
      const msg = !wasChecked && milestoneToast(Math.round((next.size / data.total) * 100));
      if (msg) toast.success(msg);
    } catch (err) {
      setDone(prev);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const applyUpto = async (e) => {
    e.preventDefault();
    const n = parseInt(upto, 10);
    if (Number.isNaN(n) || n < 0 || n > data.total) {
      toast.error(`Entre un nombre entre 0 et ${data.total}`);
      return;
    }
    try {
      const r = await axios.post(`${API}/progress/set`, { product: data.product, upto: n }, { withCredentials: true });
      setDone(new Set(Array.from({ length: n }, (_, i) => i + 1)));
      setStreak(r.data.streak);
      setUpto("");
      toast.success(n > 0 ? `Progression enregistrée : jour ${n}/${data.total}` : "Progression remise à zéro");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="activate-panel progress-panel" data-testid={`progress-panel-${data.product}`}>
      <div className="activate-head">
        <TrendingUp size={18} />
        <b>{data.title}</b>
        <span className="streak-badge" data-testid={`progress-streak-${data.product}`}>
          <Flame size={14} />
          {streak} jour{streak > 1 ? "s" : ""} d'affilée
        </span>
      </div>
      <div className="progress-meta">
        <span>{done.size}/{data.total} jours accomplis</span>
        <b data-testid={`progress-pct-${data.product}`}>{pct} %</b>
      </div>
      <div className="progress-bar-wrap" data-testid={`progress-bar-${data.product}`}>
        <motion.div
          className="progress-bar-fill"
          initial={false}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.6, ease: EASE }}
        />
      </div>
      <p className="progress-msg" data-testid={`progress-msg-${data.product}`}>{encouragement(pct, streak)}</p>
      <form onSubmit={applyUpto} className="upto-row">
        <label htmlFor={`upto-${data.product}`}>J'en suis au jour</label>
        <input
          id={`upto-${data.product}`}
          type="number"
          min="0"
          max={data.total}
          value={upto}
          onChange={(e) => setUpto(e.target.value)}
          placeholder={`1-${data.total}`}
          data-testid={`progress-upto-input-${data.product}`}
        />
        <button type="submit" className="copy-btn" data-testid={`progress-upto-btn-${data.product}`}>
          Mettre à jour
        </button>
      </form>
      <div className="days-grid" data-testid={`days-grid-${data.product}`}>
        {Array.from({ length: data.total }, (_, i) => i + 1).map((d) => (
          <button
            key={d}
            type="button"
            className={`day-dot ${done.has(d) ? "done" : ""}`}
            onClick={() => toggle(d)}
            aria-label={`Jour ${d}${done.has(d) ? " (accompli)" : ""}`}
            aria-pressed={done.has(d)}
            data-testid={`progress-day-${data.product}-${d}`}
          >
            {d}
          </button>
        ))}
      </div>
    </div>
  );
};
